import React, { useMemo } from 'react';

import { Select } from 'antd';
import type { DefaultOptionType } from 'antd/es/select/index';

import { EMPTY_MESSAGES } from '@/shared/lib/constants';
import { Icon } from '@/shared/ui/Icon';

import { formatDagVersionLabel } from '@/entities/dag';
import type { DagVersionSnapshot } from '@/entities/dag';

import styles from './DagSelectCell.module.css';

export type DagSelectCellProps = {
  dagId: string;
  value?: string;
  latestVersionLabel?: string;
  versionSnapshots: DagVersionSnapshot[];
  onChange: (dagId: string, versionLabel: string, latestVersionLabel?: string) => void;
  disabled?: boolean;
  className?: string;
};

const DagSelectCellComponent = ({
  dagId,
  value,
  latestVersionLabel,
  versionSnapshots,
  onChange,
  disabled = false,
  className,
}: DagSelectCellProps) => {
  const options = useMemo(
    () =>
      versionSnapshots.map((snapshot) => ({
        value: snapshot.versionLabel,
        label: formatDagVersionLabel(snapshot.versionLabel),
      })),
    [versionSnapshots]
  );

  const selectedValue = value ?? latestVersionLabel;
  const selectClassName = [styles.versionSelect, className].filter(Boolean).join(' ');

  const renderLabel = (props: DefaultOptionType) => (
    <span>
      {String(props.label)} (Latest: {formatDagVersionLabel(latestVersionLabel)})
    </span>
  );

  return (
    <Select
      className={selectClassName}
      variant="borderless"
      value={selectedValue}
      labelRender={renderLabel}
      onChange={(versionLabel) => onChange(dagId, versionLabel, latestVersionLabel)}
      options={options}
      aria-label={`${dagId} version`}
      popupMatchSelectWidth={false}
      notFoundContent={EMPTY_MESSAGES.NO_DAG_VERSIONS}
      classNames={{ popup: { root: styles.dropdown } }}
      suffixIcon={
        <span className={styles.arrowWrap}>
          <Icon name="arrow-drop-down" size={8} color="var(--opds-sem-color-onContainer-standard-level1)" />
        </span>
      }
      disabled={disabled}
    />
  );
};

export const DagSelectCell = React.memo(DagSelectCellComponent);

DagSelectCell.displayName = 'DagSelectCell';
